import React, { useState } from 'react';
import { DimensionValue, Image, StyleSheet } from 'react-native';

import BumoCarousel from '../components/BumoCarousel';
import CardConfigurator from '../components/CardConfigurator';
import ChildCareOfferings from '../components/ChildCareOfferings';
import Dots from '../components/Dots';
import { Footer } from '../components/Footer';
import GiftCardVoucherDetails from '../components/GiftCardVoucherDetails';
import { Container, InnerContainer } from '../components/Layout';
import Redirect from '../components/Redirect';
import { TransparentRow } from '../components/Row';
import { TextH2, TextH4 } from '../components/StyledText';
import { TransparentView, View } from '../components/Themed';
import CommonHeader from '../components/layouts/CommonHeader';
import GiftCardV2 from '../components/vouchers/GiftCardV2';
import VoucherConfigForm from '../components/vouchers/VoucherConfigForm';
import Colors from '../constants/Colors';
import FeatureFlags from '../constants/FeatureFlags';
import useColorScheme from '../hooks/useColorScheme';
import { useMaxWidth } from '../hooks/useResponsive';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const fatherCardConfig = {
  titleText: "Happy Father's day",
  amount: 50,
  backgroundColor: Colors.dark.backgroundSecondary,
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const birthdayCardConfig = {
  titleText: 'Happy Birthday',
  amount: 50,
  backgroundColor: Colors.dark.accent,
};

const mothersDayCardConfig = {
  titleText: "Happy Mother's day",
  amount: 50,
  backgroundColor: Colors.dark.accent,
};

export default function MothersDayScreen() {
  const theme = useColorScheme();
  const colors = Colors[theme];

  const isMobileDevice = useMaxWidth(500);
  const isSmallDevice = useMaxWidth(900);
  const isMediumDevice = useMaxWidth(1200);

  if (!FeatureFlags.MOTHERS_DAY_2025) {
    return <Redirect to={'Explore'} />;
  }

  return (
    <Container>
      <CommonHeader />

      <InnerContainer
        style={[styles.inner, isSmallDevice && { padding: 0, paddingTop: 40 }]}
      >
        <View style={styles.header}>
          <TextH2
            style={[styles.title, { color: colors.text, textAlign: 'center' }]}
          >
            The Mother&apos;s Day gift that counts: the gift of child care
          </TextH2>
        </View>
      </InnerContainer>

      <View style={styles.content}>
        <InnerContainer style={{ marginBottom: 50 }}>
          <Image
            source={require('../assets/images/candy.png')}
            style={{
              position: 'absolute',
              top: 0,
              right: '50%',
              width: 100,
              height: 100,
              transform: [{ translateX: 30 }],
              zIndex: 20,
            }}
          />
          <Image
            source={require('../assets/images/flower.png')}
            style={{
              position: 'absolute',
              top: 300,
              left: -50,
              width: 120,
              height: 130,
              transform: [{ translateX: 30 }],
              zIndex: 20,
            }}
          />
          <TransparentRow
            style={[
              { position: 'relative', zIndex: 100 },
              { gap: 20, alignItems: 'flex-start' },
              isSmallDevice && { flexDirection: 'column' },
              isMediumDevice && { gap: 10 },
            ]}
          >
            <TransparentView
              style={[
                !isSmallDevice && {
                  flex: 1,
                  padding: isMediumDevice ? 15 : 30,
                },
                isSmallDevice && { alignItems: 'center', width: '100%' },
              ]}
            >
              <CardConfigurator
                initialConfig={mothersDayCardConfig}
                visible={false}
              >
                {(config) => (
                  <GiftCardV2
                    style={isSmallDevice && { width: '70vw' as DimensionValue }}
                  />
                )}
              </CardConfigurator>
            </TransparentView>
            <TransparentView
              style={[
                !isSmallDevice && {
                  flex: 1,
                  padding: isMediumDevice ? 15 : 30,
                  height: '100%',
                },
                isSmallDevice && { alignItems: 'center', width: '100%' },
              ]}
            >
              <TransparentView
                style={[
                  styles.card,
                  {
                    backgroundColor: Colors[theme].background,
                  },
                  {
                    height: '100%',
                    justifyContent: 'space-between',
                    maxHeight: 600,
                  },
                  isSmallDevice && {
                    alignItems: 'center',
                    width: '100%',
                    marginTop: 20,
                  },
                ]}
              >
                <VoucherConfigForm />
              </TransparentView>
            </TransparentView>
          </TransparentRow>
        </InnerContainer>

        <TransparentView>
          <Image
            source={require('../assets/images/scribble.png')}
            style={styles.scribble}
          />
          <TextH2 style={[styles.cardTitle, { textAlign: 'center' }]}>
            Child care offerings on Bumo
          </TextH2>
          <TextH4 style={{ textAlign: 'center', marginBottom: 32 }}>
            Hundreds of locations all over California!
          </TextH4>
          <ChildCareOfferings
            Wrapper={isMobileDevice ? MobileSlider : undefined}
          />
        </TransparentView>

        <InnerContainer style={{ marginVertical: isMediumDevice ? 50 : 100 }}>
          <TextH2
            style={[
              styles.cardTitle,
              {
                textAlign: 'center',
                width: 'auto',
                alignSelf: 'center',
                position: 'relative',
              },
            ]}
          >
            {!isSmallDevice && (
              <Image
                source={require('../assets/images/bright.png')}
                style={{
                  position: 'absolute',
                  top: -90,
                  left: -130,
                  width: 120,
                  height: 100,
                  transform: [{ translateX: 30 }],
                }}
              />
            )}
            The gift of child care is the gift of time
          </TextH2>
          <TextH4 style={{ textAlign: 'center' }}>
            Just a few ways your gift can be used....
          </TextH4>
          <GiftCardVoucherDetails
            Wrapper={isMobileDevice ? MobileSlider : undefined}
          />
        </InnerContainer>
      </View>
      <Footer />
    </Container>
  );
}

const MobileSlider = ({ children }: { children: React.ReactNode }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  return (
    <>
      <BumoCarousel
        renderedElements={1}
        itemContainerStyle={{
          padding: 10,
          paddingBottom: 20,
          justifyContent: 'center',
          alignItems: 'center',
        }}
        showArrows={false}
        onIndexChange={setActiveIndex}
      >
        {children}
      </BumoCarousel>
      <Dots
        activeIndex={activeIndex}
        amount={React.Children.count(children)}
        dotProps={{
          style: {
            borderWidth: 1,
            borderColor: '#aaa',
          },
        }}
      />
    </>
  );
};

const styles = StyleSheet.create({
  inner: {
    paddingTop: 70,
  },
  scrollContent: {
    padding: 16,
  },
  header: {
    alignItems: 'center',
    marginBottom: 24,
  },
  title: {
    marginBottom: 8,
  },
  subtitle: {
    textAlign: 'center',
  },
  content: {
    gap: 16,
  },
  card: {
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    shadowColor: Colors.dark.shadow,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 1,
    shadowRadius: 10,
    height: '100%',
    textAlign: 'center',
    alignItems: 'center',
  },
  cardTitle: {
    marginBottom: 8,
  },
  cardText: {
    marginBottom: 16,
    lineHeight: 20,
  },
  button: {
    marginTop: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 4,
    padding: 8,
  },
  form: {
    flex: 1,
  },
  scribble: {
    marginHorizontal: 'auto',
    maxWidth: 350,
    width: '90%',
    marginBottom: 40,
  },
});
